import React from 'react';

import { InvoiceWarning } from 'entities/Invoice';
import { format } from 'utils/format';

interface Row {
  label: string;
  previousInvoiceCurrent: number;
  thisInvoicePrevious: number;
}

const parseReading = (value: string) => Number(value.replace(/\./g, ''));

/**
 * Registradores do aviso "leitura não continua a fatura anterior". Avisos
 * gravados desde 30/set/2026 trazem `registers`; os anteriores só têm o texto
 * corrido ("consumo Fora Ponta: a fatura de 06/2026 terminou em 47.645 e esta
 * começa em 104.191; …"), que é lido aqui para a tabela funcionar sem
 * reprocessar as faturas.
 */
export const readingDiscontinuityRows = (
  w: InvoiceWarning
): { rows: Row[]; previousRefMonth: string } => {
  if (w.registers?.length) {
    return { rows: w.registers, previousRefMonth: w.previousRefMonth ?? '' };
  }
  const re =
    /([^;(]+?): a fatura de (\d{2}\/\d{4}) terminou em ([\d.]+) e esta começa em ([\d.]+)/g;
  const rows: Row[] = [];
  let previousRefMonth = '';
  let m: RegExpExecArray | null;
  while ((m = re.exec(w.message)) !== null) {
    const label = m[1].trim();
    previousRefMonth = `${m[2].slice(3, 7)}_${m[2].slice(0, 2)}`; // MM/AAAA → AAAA_MM
    rows.push({
      label: label.charAt(0).toUpperCase() + label.slice(1),
      previousInvoiceCurrent: parseReading(m[3]),
      thisInvoicePrevious: parseReading(m[4]),
    });
  }
  return { rows, previousRefMonth };
};

const MONTHS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

// AAAA_MM → { num: '06/26', name: 'Jun/26' }
const monthLabels = (refMonth: string) => {
  const [year, month] = refMonth.split('_');
  if (!year || !month) return null;
  const yy = year.slice(2);
  return {
    num: `${month}/${yy}`,
    name: `${MONTHS[Number(month) - 1] ?? month}/${yy}`,
  };
};

// Mês anterior a AAAA_MM (quando só a fatura atual é conhecida)
const previousOf = (refMonth: string) => {
  const [y, m] = refMonth.split('_').map(Number);
  if (!y || !m) return '';
  return m === 1 ? `${y - 1}_12` : `${y}_${String(m - 1).padStart(2, '0')}`;
};

const cell: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid #f79422',
  textAlign: 'right',
};

/** Tabela dos registradores cuja leitura não continua a fatura anterior. */
export const ReadingDiscontinuityTable = ({
  warning,
  refMonth,
}: {
  warning: InvoiceWarning;
  // Mês desta fatura (AAAA_MM) — rótulo da 2ª coluna
  refMonth?: string;
}) => {
  const { rows, previousRefMonth } = readingDiscontinuityRows(warning);
  if (!rows.length) return null;

  // Cabeçalhos "Leitura (06/26) - Fatura de Jun/26" e "Leitura (06/26) -
  // Fatura de Jul/26" (decisão do Bruno 30/set/2026): é a MESMA leitura (a
  // de fechamento de junho), vista em cada fatura.
  const prev = monthLabels(previousRefMonth || previousOf(refMonth ?? ''));
  const curr = refMonth ? monthLabels(refMonth) : null;
  const reading = prev ? `Leitura (${prev.num})` : 'Leitura';
  const previousHeader = prev
    ? `${reading} - Fatura de ${prev.name}`
    : 'Última leitura da fatura anterior';
  const currentHeader = curr
    ? `${reading} - Fatura de ${curr.name}`
    : 'Primeira leitura desta fatura';

  // Contêiner com rolagem horizontal: no celular a tabela rola dentro do
  // bloco em vez de alargar a página (relatório responsivo, out/2026). A
  // margem saiu da tabela para o contêiner — espaçamento igual ao de antes.
  return (
    <div style={{ overflowX: 'auto', margin: '10px 0' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
          background: '#FFFFFF',
          borderRadius: 3,
        }}
      >
        <thead>
          <tr style={{ fontWeight: 600 }}>
            <th style={{ ...cell, textAlign: 'left' }}>Registrador</th>
            <th style={cell}>{previousHeader}</th>
            <th style={cell}>{currentHeader}</th>
            <th style={cell}>Diferença</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const diff = r.thisInvoicePrevious - r.previousInvoiceCurrent;
            return (
              <tr key={r.label}>
                <td style={{ ...cell, textAlign: 'left' }}>{r.label}</td>
                <td style={cell}>{format.energy(r.previousInvoiceCurrent)}</td>
                <td style={cell}>{format.energy(r.thisInvoicePrevious)}</td>
                <td style={{ ...cell, fontWeight: 700 }}>
                  {diff > 0 ? '+' : diff < 0 ? '−' : ''}
                  {format.energy(Math.abs(diff))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
